/**
 * THE PERMISSION-REQUIREMENT PRIMITIVES — the shapes a permission check is asked in, with no vocabulary
 * of their own.
 *
 * A ZERO-IMPORT LEAF, deliberately. These ten were declared in `permissionList.ts`, which is the LEGACY
 * name list and is being deleted; every one of them is system-agnostic and is used by the granular
 * system, by uikit, by orion and by the hiring subgraph. Left where they were, deleting the legacy list
 * would take the type that makes an empty requirement a compile error with it.
 *
 * `permissionKeys.ts` imports `NonEmptyPermissions` from here rather than from `permissionList.ts`, so
 * the dependency runs from both vocabularies INTO this file and never the other way. Nothing here may
 * import either vocabulary.
 *
 * `BYPASS_PERMISSION` is `as const` and not annotated, and that is load-bearing rather than stylistic:
 * annotate it `: string` and `BypassPermissionName` widens to `string`, `withoutBypass` then accepts any
 * typo, and all fourteen guards that refuse the platform bypass silently stop refusing it. It also lost
 * its `satisfies AudienceSafePermissionName` in the move, for the reason this file exists — that type is
 * the legacy union and does not survive.
 */

/**
 * The one permission that satisfies every check on its own: `User.hasPermission` returns true for a
 * holder whatever was asked, and both granular evaluators short-circuit on it.
 *
 * A guard that must NOT admit a platform administrator therefore has to refuse it BY NAME — see
 * `PermissionCheckOptions.withoutBypass`. Named rather than expressed as a boolean so the refusal is
 * greppable, and so renaming the permission is a compile error at every site that refuses it.
 */
export const BYPASS_PERMISSION = 'portal.superadmin' as const;

/**
 * What `withoutBypass` may name. Deliberately the bypasses that EXIST rather than the whole
 * permission vocabulary: a name that bypasses nothing would refuse nothing, and would do it silently.
 */
export type BypassPermissionName = typeof BYPASS_PERMISSION;

/**
 * A permission REQUIREMENT: at least one name, and never zero.
 *
 * The type exists because an empty list is the single most repeated defect shape in this repo, and it
 * cannot be caught by reading the call site: `[].every()` is TRUE, so the all-of reading of a list of
 * nothing granted to everybody, while `[].some()` on the identical input refused. Four primitives
 * disagreed about what `[]` meant — refuse, refuse, contribute-nothing, and grant — which is exactly
 * the state a reader cannot hold in their head.
 *
 * Spelled as a tuple rather than a length check so an empty literal is a COMPILE error, at every site,
 * in every subgraph. Nothing enforces this at runtime: `EMPTY_REQUIREMENT_LOG` is the runtime half, for
 * a list that arrives through a variable the type could not see.
 */
export type NonEmptyPermissions<T extends string> = readonly [T, ...T[]];

/**
 * A requirement ANY ONE of whose names satisfies the check, where the PRIMITIVE fixes that reading
 * rather than the caller choosing it.
 *
 * The same type as `NonEmptyPermissions` — TypeScript cannot tell them apart and is not meant to. It is
 * the field's DOCUMENTATION, at the one place a reader looks: the client-access guards' company-wide arm
 * replaces a disjunction only, so `any` is not its to be asked, and a bare `readonly T[]` on that field
 * is what made a reader guess. Where the quantifier IS a choice — `PermissionRequest`,
 * `SyncPermissionRequest` — the call site states it, and the note below says what enforces that.
 */
export type AnyOfPermissions<T extends string> = NonEmptyPermissions<T>;

/**
 * WHICH TIER OF CLIENT ACCESS a guard requires — the whole of it, because the array of access LEVELS
 * this replaces offered a combinatorial space in which exactly two points were ever used.
 *
 * Measured across both subgraphs: 28 server sites and 33 client sites want `full` OR `limited`, 24
 * server sites and 122 client sites want `full` alone, and `['limited']` on its own appears **nowhere**.
 * The array's cost was its empty case: `levels: []` accepts nothing, and an OMITTED `permissionLevels`
 * in uikit admitted every employee — a live hole at 11 call sites.
 *
 * `'limited'` IS NOT A VALUE HERE, and that is the point rather than an omission. The two levels are
 * compared as equal alternatives (`levels.includes(row.permissionType)`), so a requirement of exactly
 * `limited` would REFUSE a caller holding `full`, which is not what any site has ever meant. What the
 * two-level sites mean is "at least limited" — any reach at all — and that is the name.
 *
 * `'full'` is spelled as it always was so the 122 sites already saying `full` say the same thing after
 * the array is gone; it is a REQUIREMENT here rather than a level, and nothing takes a level any more.
 */
export type ClientAccessRequirement = 'full' | 'atLeastLimited';

/**
 * THE ABSENCE OF A PERMISSION ARM, as a value — distinct from an empty requirement, which is a bug.
 *
 * Many guards are a disjunction of a permission arm and a second axis: the client account
 * (`hasClientScopedPermission`, `ClientPagePermission`), a relationship, or nothing at all (a nav item
 * with no requirement). On 25 of the client-scoped ones the permission arm does not exist — the account
 * axis is the whole guard — and that was spelled `legacy: [], permission: []`, sharing one
 * representation with the empty-requirement BUG. So the same two characters meant "deny" in one
 * primitive, "contribute nothing" in another, and "admit everybody" in a third, and no call site said
 * which.
 *
 * Naming the absence separates them. `[]` is a compile error now (`NonEmptyPermissions`), and a guard
 * whose arm is genuinely absent says so — which is also the grep that finds all of them.
 *
 * THE NEUTRAL ELEMENT IS THE CONNECTIVE'S, NOT THIS VALUE'S, and that is why nothing here decides it:
 * an absent arm contributes `false` to a disjunction and `true` to a conjunction. `usePermissions`
 * returns false for it and the COMPONENT asks separately whether the arm is there — see
 * `ClientPagePermission` and orion's `NavLeftV2Item`, which are the two worked examples.
 *
 * IT IS NOT `NO_LEGACY_CHECK`, and the two must not merge. That one says "today's system checked nothing
 * here, so the flag-OFF half must admit"; this one says "there is no permission arm in EITHER set, in
 * either flag position". One is a transition artefact that dies with the dual-run; this one is a
 * permanent property of the guard.
 */
export const NO_PERMISSION_ARM = 'no-permission-arm';

/** How a permission check is qualified. Every field is optional, so omitting it is today's behaviour. */
export type PermissionCheckOptions = {
  /** With a LIST of names, hold ANY one of them. Absent, a list requires every name. */
  any?: boolean;
  /**
   * Refuse the named bypass, so holding it and nothing else does not satisfy this check. This is how a
   * guard says "not reachable by a bare platform administrator" — the shape that used to be a `notSuper`
   * boolean in third position and could not be expressed in the new permission set at all.
   */
  withoutBypass?: BypassPermissionName;
};

/*
 * THE MULTI-KEY QUANTIFIER RULE LIVES IN A TEST, NOT IN A TYPE, and it is worth saying why here because
 * this is where a reader looks for it.
 *
 * The rule: a requirement naming two or more keys must state its own reading. There is no default to
 * fall back on — `hasPermission` defaulted a list to ALL-OF while uikit's identical-looking
 * `hasPermissions` defaulted to ANY-OF, so the same two names meant a conjunction on the server and a
 * disjunction in the browser, and flipping either default moved people (that lockout shipped three
 * times, 18 people the last time).
 *
 * `QuantifierFor` and `PermissionQuantifier` used to encode it as a rest-parameter tuple, and they are
 * deleted rather than kept as documented-but-dead primitives. They could only ever constrain POSITIONAL
 * arguments, and every guard site in every subgraph now passes an object literal — a `PermissionRequest`
 * — whose `any` the type cannot see or require. So they had zero consumers, while two comments cited
 * them as the reason a multi-key list is safe. A type that names the wrong protector is worse than no
 * type: it tells a reader the compiler is watching when it is not.
 *
 * What actually watches is `guardSitePopulations.test.ts`'s `makes every multi-key request state its own
 * quantifier, so no disjunction becomes a conjunction` — a source-reading assertion over every
 * registered guard file, which fails CI on a multi-key request with no `any`. `any` stays an ordinary
 * optional field on `PermissionCheckOptions` above.
 */

/**
 * Logged when a permission check is handed an EMPTY list of names.
 *
 * An empty requirement is a programming error, never "no requirement, therefore allowed", and every
 * primitive that can be handed one refuses it. It is logged rather than thrown because these checks run
 * per field, hundreds of times a request: a refusal is a 403, a throw would be a 500 on every field
 * behind it. Nothing in the tree reaches this today, so a line here is a bug and not noise.
 *
 * One greppable string, shared by both implementations of the check, for the same reason
 * `SUPERADMIN_BYPASS_LOG` is one — the aggregator needs a phrase nobody retypes.
 */
export const EMPTY_REQUIREMENT_LOG = 'Permission check refused: the requirement list was empty';
